import { useRef, useState } from 'react'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import { useSWRConfig } from 'swr'
import { twJoin } from 'tailwind-merge'
import Badge from 'shared/components/badge'
import { useMediaQuery } from 'shared/hooks/use-match-media'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { LectureAssignmentFileIcon } from 'shared/icons/lecture-assignment-file-icon'
import { HTMLString } from 'shared/types/html-string'
import { convertBytesToMB } from 'shared/utils/convert-bytes-to-mb'
import { truncateMiddle } from 'shared/utils/truncate-middle'
import { useSubmitAssignment } from 'modules/course/api/submit-assignment'
import { getCourseLectureKey } from 'modules/course/hooks/use-course-lecture'
import { getCourseMenuKey } from 'modules/course/hooks/use-course-menu'
import { CourseId } from 'modules/course/types/course-interface'
import {
  CourseLectureAssignmentInterface,
  CourseLectureId,
} from 'modules/course/types/course-lecture-interface'
import { CourseLectureAssignmentStatusEnum } from 'modules/course/types/course-menu-interface'
import { PrimaryButtonWithCustomTheme } from '../primary-button-with-custom-theme'
import { SecondaryButtonWithCustomTheme } from '../secondary-button-with-custom-theme'
import { AssignmentsStatusTranslations } from './constants'
import { AssignmentUploader } from './course-lecture-assignment-uploader'
import { SubmitAssignmentModal } from './submit-assignment-modal'
import { AssignmentUploadingStatusEnum, useUploadAssignment } from './use-upload-assignment'
import {
  convertAssignmentDate,
  getAssignmentStatusBackgroundColor,
  getAssignmentStatusTextColor,
} from './utils'

interface CourseLectureAssignmentProps {
  lectureId: CourseLectureId
  courseId?: CourseId
  assigmentTitle?: string
  assignmentDescription?: HTMLString
  assignment: CourseLectureAssignmentInterface | null
  searchQuery?: string
}

export const CourseLectureAssignment = ({
  lectureId,
  courseId,
  assignment,
  assigmentTitle,
  assignmentDescription,
  searchQuery,
}: CourseLectureAssignmentProps) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const isMobile = useMediaQuery('(max-width: 768px)', false)
  const { mutate } = useSWRConfig()
  const [isAssignmentConfirmationOpened, setIsAssignmentConfirmationOpened] = useState(false)
  const [isReuploadAssignmentStep, setIsReuploadAssignmentStep] = useState(false)

  const assignmentStatus = assignment
    ? assignment?.review?.status || CourseLectureAssignmentStatusEnum.Pending
    : null

  const isAssignmentSubmitted =
    !!assignmentStatus &&
    [
      CourseLectureAssignmentStatusEnum.Approved,
      CourseLectureAssignmentStatusEnum.Rejected,
      CourseLectureAssignmentStatusEnum.Pending,
    ].includes(assignmentStatus)

  const isAssignmentCancelled =
    !!assignmentStatus &&
    [
      CourseLectureAssignmentStatusEnum.Rejected,
      CourseLectureAssignmentStatusEnum.NotSubmitted,
    ].includes(assignmentStatus)

  const isAssignmentPending = assignmentStatus === CourseLectureAssignmentStatusEnum.Pending

  const { file, uploadStatus, handleFileUpload, progress, cancelUpload } =
    useUploadAssignment(assignment)
  const { trigger: submitAssignment } = useSubmitAssignment(lectureId)

  const onSubmitAssignment = async () => {
    await submitAssignment(
      { dataFileId: file?.id },
      {
        onSuccess: () => {
          mutate(getCourseMenuKey(courseId))

          mutate(
            getCourseLectureKey({ courseLectureId: lectureId, searchQuery }),
            prev => ({
              ...prev,
              assignment: {
                ...prev?.assignment,
                ...file,
                dataFileId: file?.id,
                review: {
                  ...assignment?.review,
                  status: CourseLectureAssignmentStatusEnum.Pending,
                },
              },
            }),
            { revalidate: false },
          )

          setIsReuploadAssignmentStep(false)
        },
      },
    )
  }

  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = () => {
    inputRef?.current?.click()
  }
  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length && e.target.files?.length > 0) {
      await handleFileUpload(e.target.files[0])
    }
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.dataTransfer.files.length > 0) {
      await handleFileUpload(e.dataTransfer.files[0])
    }
  }

  return (
    <div>
      <div className="mt-5 rounded-md bg-THEME-block-background p-1.5 md:p-6">
        <p className="mb-4 text-lg font-bold text-THEME-primary">{assigmentTitle}</p>
        <div
          className="break-words"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(assignmentDescription || '') }}
        ></div>
      </div>

      <div className="mt-5 rounded-md bg-THEME-block-background p-1.5 md:p-6">
        {/* First time uploading assignment file */}
        {!isAssignmentSubmitted && !isReuploadAssignmentStep && (
          <>
            <AssignmentUploader
              handleClick={handleClick}
              handleInputChange={handleInputChange}
              handleDrop={handleDrop}
              ref={inputRef}
              uploadingStatus={uploadStatus}
              file={file}
              progress={progress}
              cancelUpload={cancelUpload}
            />
            <PrimaryButtonWithCustomTheme
              disabled={!file || uploadStatus !== AssignmentUploadingStatusEnum.Uploaded}
              onClick={() => {
                setIsAssignmentConfirmationOpened(true)
              }}
              className="mt-6 w-fit"
            >
              {t('course.lecture.assignment.submit')}
            </PrimaryButtonWithCustomTheme>
          </>
        )}

        {/* Reuploading assignment file after rejection */}
        {isReuploadAssignmentStep && (
          <>
            <AssignmentUploader
              handleClick={handleClick}
              handleInputChange={handleInputChange}
              handleDrop={handleDrop}
              ref={inputRef}
              uploadingStatus={uploadStatus}
              file={file}
              progress={progress}
              cancelUpload={cancelUpload}
            />
            <div className="mt-6 flex gap-4">
              <SecondaryButtonWithCustomTheme
                className="h-fit"
                onClick={() => {
                  setIsReuploadAssignmentStep(false)
                  cancelUpload()
                }}
              >
                {t('course.lecture.assignment.cancel')}
              </SecondaryButtonWithCustomTheme>
              <PrimaryButtonWithCustomTheme
                disabled={!file || uploadStatus !== AssignmentUploadingStatusEnum.Uploaded}
                onClick={() => {
                  setIsAssignmentConfirmationOpened(true)
                }}
                className="w-fit"
              >
                {t('course.lecture.assignment.submit')}
              </PrimaryButtonWithCustomTheme>
            </div>
          </>
        )}

        {/* Uploaded assignment file info */}
        {isAssignmentSubmitted && !isReuploadAssignmentStep && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-2.5 rounded-md border border-text-primary/30 p-1.5 md:grid md:grid-cols-3 md:p-5">
              <div className="flex w-fit gap-2.5">
                <div className="flex items-center rounded-md border border-text-primary/30 p-2.5">
                  <LectureAssignmentFileIcon className="text-THEME-secondary" />
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {truncateMiddle(file?.filename, {
                      maxLength: isMobile ? 10 : 20,
                      prefixLength: isMobile ? 3 : 10,
                      suffixLength: isMobile ? 5 : 7,
                    })}
                  </span>
                  <span className="text-THEME-primary/60">{convertBytesToMB(file?.size)}</span>
                </div>
              </div>

              <span className="text-THEME-primary/60 text-center text-sm">
                {isAuthorizedUser(user) &&
                  convertAssignmentDate(file?.createdAt, {
                    dateFormat: user.dateFormat,
                    timeZone: user.timezone,
                    locale: user.dashboardLocale,
                  })}
              </span>

              <div className="w-fit justify-self-end">
                <Badge
                  className={twJoin(
                    'rounded-full px-2.5 py-1.5 capitalize',
                    getAssignmentStatusBackgroundColor(assignmentStatus),
                    getAssignmentStatusTextColor(assignmentStatus),
                  )}
                >
                  {isAssignmentPending
                    ? t('course.lecture.assignment.pending')
                    : t(AssignmentsStatusTranslations[assignmentStatus])}
                </Badge>
              </div>
            </div>
            <div>
              {!isAssignmentPending && assignment?.review?.comment && (
                <p className="font-semibold text-THEME-primary">
                  {t('course.lecture.assignment.feedback')}
                </p>
              )}
              <div
                dangerouslySetInnerHTML={{
                  __html: isAssignmentPending
                    ? t('course.lecture.assignment.default_review_text')
                    : DOMPurify.sanitize(assignment?.review?.comment || ''),
                }}
              />
            </div>
            {isAssignmentCancelled && !isReuploadAssignmentStep && (
              <PrimaryButtonWithCustomTheme
                onClick={() => {
                  setIsReuploadAssignmentStep(true)
                }}
                className="w-fit"
              >
                {t('course.lecture.assignment.resubmit')}
              </PrimaryButtonWithCustomTheme>
            )}
          </div>
        )}

        {isAssignmentConfirmationOpened && (
          <SubmitAssignmentModal
            onCloseModal={() => setIsAssignmentConfirmationOpened(false)}
            onConfirm={onSubmitAssignment}
            confirmationContent={t('course.lecture.assignment.confirmation.content')}
            title={'course.lecture.assignment.submit'}
          />
        )}
      </div>
    </div>
  )
}
